import { useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import {
  LayoutDashboard, Package, ShieldCheck, LogOut, Menu, X, ArrowUpRight,
} from "lucide-react";

const NAV = [
  { to: "/business-app", end: true, icon: LayoutDashboard, label: "Tableau de bord" },
  { to: "/business-app/catalogue", icon: Package, label: "Catalogue produits" },
  { to: "/business-app/revendications", icon: ShieldCheck, label: "Revendications" },
];

export default function BusinessShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user?.business) return <Navigate to="/app/business" replace />;

  const doLogout = () => { logout(); navigate("/"); };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-5">
        <button onClick={() => navigate("/")} data-testid="biz-logo-home" aria-label="Accueil" className="flex items-center gap-2">
          <Logo />
        </button>
        <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-indigo-500/15 text-indigo-300 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1">
          <Package size={11} /> Console Business
        </span>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`biznav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                isActive ? "bg-indigo-500/15 text-indigo-300" : "text-muted-foreground hover:text-foreground hover:bg-white/5"
              }`}>
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
        <button data-testid="biz-to-personal" onClick={() => navigate("/app")}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-white/5">
          <ArrowUpRight size={18} /> Mon espace personnel
        </button>
      </nav>
      <div className="p-3 border-t border-white/5">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-9 h-9 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-300 font-medium">
            {(user?.business?.company_name || user?.name || "?")[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{user?.business?.company_name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.business?.account_type} · {user?.business?.plan}</p>
          </div>
          <button onClick={doLogout} data-testid="biz-logout-btn" className="text-muted-foreground hover:text-destructive">
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-white/5 omy-glass sticky top-0 h-screen">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 omy-glass h-full"><SidebarContent /></div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-40 omy-glass border-b border-white/5 h-14 flex items-center justify-between px-4">
          <button onClick={() => navigate("/")} data-testid="biz-logo-home-mobile" aria-label="Accueil"><Logo size={24} /></button>
          <button data-testid="biz-mobile-menu-btn" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
        </div>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
